import { makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from '@fluentui/react-components';
import { CalendarToday, ChevronLeft, ChevronRight } from '@material-symbols-svg/react/rounded/w500';

import { nextMonth, prevMonth, useCurrentMonth } from '../calendar';
import { palette, tokens } from '../theme';

const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }));

export const MonthPicker: React.FC = () => {
    const classes = useStyles();
    const { current, set, goToday, label } = useCurrentMonth();

    return (
        <div className={classes.cluster}>
            <div className={classes.group} role="group" aria-label="Month navigation">
                <button type="button" className={classes.arrow} aria-label="Previous month" onClick={() => set(prevMonth(current))}>
                    <ChevronLeft className={classes.arrowIcon} aria-hidden />
                </button>
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <button type="button" className={classes.month} aria-label="Pick a month">
                            <span className={classes.monthName}>{label.month}</span>
                            <span className={classes.yearName}>{label.year}</span>
                        </button>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {MONTH_NAMES.map((name, idx) => (
                                <MenuItem key={name} onClick={() => set({ year: current.year, month: idx + 1 })}>
                                    {name} {current.year}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </MenuPopover>
                </Menu>
                <button type="button" className={classes.arrow} aria-label="Next month" onClick={() => set(nextMonth(current))}>
                    <ChevronRight className={classes.arrowIcon} aria-hidden />
                </button>
            </div>
            <Tooltip content="Jump to today" relationship="label">
                <button type="button" className={classes.todayButton} aria-label="Jump to today" onClick={goToday}>
                    <CalendarToday className={classes.todayIcon} aria-hidden />
                </button>
            </Tooltip>
        </div>
    );
};

const useStyles = makeStyles({
    cluster: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    group: {
        display: 'inline-flex',
        alignItems: 'stretch',
        height: '38px',
        borderRadius: palette.pillRadius,
        backgroundColor: palette.panelBg,
        boxShadow: palette.softShadowSm,
        overflow: 'hidden',
        transition: 'box-shadow 160ms ease',
        '&:hover': {
            boxShadow: palette.softShadowMd,
        },
    },
    arrow: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        border: 'none',
        background: 'transparent',
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        padding: 0,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
        // Focus outline dropped — the surrounding pill clips with overflow:hidden,
        // so a rectangular outline intersects the rounded corners. The backgroundColor
        // flip carries the keyboard-focus signal instead.
        '&:focus-visible': {
            outline: 'none',
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
    },
    month: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0 16px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '15px',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: 1.1,
        color: tokens.colorNeutralForeground1,
        transition: 'background-color 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
        },
        '&:focus-visible': {
            outline: 'none',
            backgroundColor: palette.accentBg,
        },
    },
    monthName: {
        color: tokens.colorNeutralForeground1,
    },
    yearName: {
        fontWeight: 300,
        color: tokens.colorNeutralForeground3,
    },
    todayButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '34px',
        height: '34px',
        marginLeft: '6px',
        border: 'none',
        borderRadius: '50%',
        backgroundColor: palette.panelBg,
        boxShadow: palette.softShadowSm,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        padding: 0,
        transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
            boxShadow: palette.softShadowMd,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '2px',
        },
    },
    todayIcon: {
        width: '16px',
        height: '16px',
    },
    arrowIcon: {
        width: '18px',
        height: '18px',
    },
});
